import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import baseAPI from "../../../services/api";
import { BASE_URL } from "../../../services/config";
import { Organization } from "../../../services/organization/organizationModel";
import { PremiumSponsorship } from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../../services/premiumSponsorship/premiumSponsorshipService";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import useReload from "../../../hooks/useReload";
import ManagePremiumSponsorshipDialog, {
  SponsorshipStatusChip,
} from "./ManagePremiumSponsorshipDialog";
import SortableTh from "../../../components/SortableTh";
import { TableSort } from "../../../react-helpers/array";

const { deletePremiumSponsorship } = premiumSponsorshipService();

export type SponsorshipsSortKey = "status";

// Pastille d'initiales (maquette : avatar), bleu nuit pour tous (Nicolas, 01/11 : une couleur par candidat compliquait).
const avatarInitials = (email: string) =>
  email
    .split("@")[0]
    .split(/[._\-+\s]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

// Vignette du candidat comme sur le module stats (Nicolas, 01/11), initiales à défaut (pas de profil, pas de
// vignette, ou candidat qui n'a pas consenti au partage de ses données).
const SponsorAvatar = ({ email, uid }: { email: string; uid?: string }) => {
  const [failed, setFailed] = useState(false);
  return uid && !failed ? (
    <img
      className="sp-av --img"
      src={`${BASE_URL}/profiles/${uid}/thumbnail`}
      alt=""
      onError={() => setFailed(true)}
    />
  ) : (
    <span className="sp-av" aria-hidden="true">
      {avatarInitials(email)}
    </span>
  );
};

const PremiumSponsorshipsTable = ({
  organization,
  sponsorships,
  sort,
  onSort,
}: {
  organization: Organization;
  sponsorships: PremiumSponsorship[];
  sort: TableSort<SponsorshipsSortKey> | null;
  onSort: (key: SponsorshipsSortKey) => void;
}) => {
  const { t } = useTranslation(["organization"]);
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();
  const [managedSponsorship, setManagedSponsorship] =
    useState<PremiumSponsorship | null>(null);

  // Profil principal des candidats accompagnés (acceptés, avec consentement) : la plateforme ne le renvoie pas,
  // notre API si (même origine, même jeton, comme les droits de ProShell.tsx). En cas d'échec : initiales.
  const [profileUids, setProfileUids] = useState<Record<number, string>>({});
  useEffect(() => {
    baseAPI
      .get<{ userId: number; mainProfileUid: string | null }[]>(
        "/dashboard/accompanied-candidates",
      )
      .then(({ data }) =>
        setProfileUids(
          Object.fromEntries(
            data
              .filter((c) => c.mainProfileUid)
              .map((c) => [c.userId, c.mainProfileUid!]),
          ),
        ),
      )
      .catch(() => undefined);
  }, []);

  const headers = {
    email: t("organization:premium-sponsorship.table-headers.EMAIL"),
    status: t("organization:premium-sponsorship.table-headers.STATUS"),
    sentDate: t("organization:premium-sponsorship.table-headers.SENT_DATE"),
  };

  return (
    <>
      {sponsorships.length > 0 ? (
        <div className="sp-table">
          <table className="table">
            <thead>
              <tr>
                <th>{headers.email}</th>
                <SortableTh sortKey="status" sort={sort} onSort={onSort}>
                  {headers.status}
                </SortableTh>
                <th>{headers.sentDate}</th>
                <th className="sp-table_actions">
                  {t("organization:premium-sponsorship.table-headers.ACTION")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sponsorships.map((sponsorship) => (
                <tr key={`premium-sponsorship-${sponsorship.id}`}>
                  <td className="sp-table_full">
                    <span className="sp-who">
                      <SponsorAvatar
                        email={sponsorship.email}
                        uid={
                          sponsorship.userId
                            ? profileUids[sponsorship.userId]
                            : undefined
                        }
                      />
                      <b>{sponsorship.email}</b>
                    </span>
                  </td>
                  <td data-label={headers.status}>
                    <SponsorshipStatusChip status={sponsorship.status} />
                  </td>
                  <td data-label={headers.sentDate} className="ui-num">
                    {format(new Date(sponsorship.invitedAt), "dd/MM/yyyy")}
                  </td>
                  <td className="sp-table_full sp-table_actions">
                    <div className="sp-actions">
                      <button
                        className="btn--soft --btn--s sp-manage"
                        title={t("organization:premium-sponsorship.MANAGE_TIP")}
                        onClick={() => {
                          setManagedSponsorship(sponsorship);
                        }}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" />
                          <path d="m13.5 6.5 4 4" />
                        </svg>
                        {t("organization:premium-sponsorship.MANAGE_BUTTON")}
                      </button>
                      <button
                        className="sp-delete"
                        title={t("organization:premium-sponsorship.DELETE_TIP")}
                        aria-label={t(
                          "organization:premium-sponsorship.DELETE_TIP",
                        )}
                        onClick={() => {
                          confirm(
                            "organization:premium-sponsorship.confirm-delete.MESSAGE",
                            () =>
                              deletePremiumSponsorship(
                                organization.id,
                                sponsorship.id,
                              ).then(() => reload()),
                            true,
                            {
                              email: sponsorship.email,
                            },
                          );
                        }}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M4 7h16M9 7V4.5h6V7M6 7l1 13.5h10L18 7" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="sp-table sp-empty">
          <div className="sp-empty_ill">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </div>
          <b>{t("organization:premium-sponsorship.NO_SPONSORSHIPS_FOUND")}</b>
        </div>
      )}

      {managedSponsorship && (
        <ManagePremiumSponsorshipDialog
          onClose={() => setManagedSponsorship(null)}
          organization={organization}
          sponsorship={managedSponsorship}
        />
      )}
    </>
  );
};

export default PremiumSponsorshipsTable;
